import type { Board } from "../engine/board";
import { MovementEffect } from "../engine/effects/movementEffect";
import { XYEffect } from "../engine/effects/xyEffect";
import { isTintable } from "../engine/interfaces";
import type { PhysicalThing } from "../engine/physicalThing";
import { XY } from "../engine/xy";
import type { AnimationLoader } from "./animationLoader";
import type { Camera } from "./camera";
import type { DrawableManager } from "./drawableManager";
import type { EffectManager } from "./effectManager";
import { entityHasMovementEffect, resolveEntityVisualCell, resolveXYEffectDrawPosition } from "./movementInterpolation";
import * as TileImageLoader from "./tileImageLoader";
import { getTintedImage } from "./tintCache";

// Row dominates z-order within the draw sort; load-bearing, see decisions.md SlidingDoor.
const ROW_SORT_WEIGHT = 100;

export class SpriteRenderer {
	private ctx: CanvasRenderingContext2D;
	private camera: Camera;
	private drawableManager: DrawableManager;
	private animationLoader: AnimationLoader;
	private gridTileImage: HTMLCanvasElement | null = null;
	private floorImages: Map<string, HTMLImageElement | HTMLCanvasElement | null> = new Map();
	private currentDefaultFloor: string | null = null;
	private floorCacheCanvas: HTMLCanvasElement | null = null;
	private followTarget: PhysicalThing | null = null;
	private elapsedTime: number = 0;
	private lastRenderTimestamp: number | null = null;

	// Performance counters (reset each render call)
	lastFloorDrawCalls: number = 0;
	lastThingDrawCalls: number = 0;
	lastVisibleCells: number = 0;
	lastDrawableCount: number = 0;

	constructor(
		ctx: CanvasRenderingContext2D,
		camera: Camera,
		drawableManager: DrawableManager,
		animationLoader: AnimationLoader,
	) {
		this.ctx = ctx;
		this.camera = camera;
		this.drawableManager = drawableManager;
		this.animationLoader = animationLoader;
	}

	setFollowTarget(target: PhysicalThing | null): void {
		this.followTarget = target;
	}

	async loadFloorTile(): Promise<void> {
		try {
			this.gridTileImage = await TileImageLoader.getImage(
				"GridTile.png",
				this.camera.cellWidth,
				this.camera.cellHeight,
			);
		} catch (e) {
			console.warn("Failed to load GridTile.png, falling back to solid fill", e);
			this.gridTileImage = null;
		}
	}

	private async loadFloorVariants(key: string, board: Board): Promise<void> {
		if (this.currentDefaultFloor === key) return;
		this.currentDefaultFloor = key;
		this.floorImages.clear();

		const variantSet = new Set<number>();
		for (let x = 0; x < board.width; x++) {
			for (let y = 0; y < board.height; y++) {
				variantSet.add(board.terrainVisualIds[x][y]);
			}
		}

		for (const visualId of variantSet) {
			const cacheKey = `${key}:${visualId}`;
			const anim = await this.animationLoader.getAnimationByKey(key, visualId);
			if (anim && anim.frames.length > 0) {
				this.floorImages.set(cacheKey, anim.frames[0].sprite.image);
			}
		}
	}

	async syncFloor(board: Board): Promise<void> {
		if (board.defaultFloor) {
			await this.loadFloorVariants(board.defaultFloor, board);
		} else {
			this.currentDefaultFloor = null;
			this.floorImages.clear();
		}
		this.buildFloorCache(board);
	}

	private buildFloorCache(board: Board): void {
		const w = board.width * this.camera.cellWidth;
		const h = board.height * this.camera.cellHeight;

		if (!this.floorCacheCanvas || this.floorCacheCanvas.width !== w || this.floorCacheCanvas.height !== h) {
			this.floorCacheCanvas = document.createElement("canvas");
			this.floorCacheCanvas.width = w;
			this.floorCacheCanvas.height = h;
		}

		const cacheCtx = this.floorCacheCanvas.getContext("2d")!;
		cacheCtx.clearRect(0, 0, w, h);

		for (let y = 0; y < board.height; y++) {
			for (let x = 0; x < board.width; x++) {
				const floorImg = this.getFloorImage(board, x, y);
				const px = x * this.camera.cellWidth;
				const py = y * this.camera.cellHeight;
				if (floorImg) {
					cacheCtx.drawImage(floorImg, px, py);
				} else {
					cacheCtx.fillStyle = "#111";
					cacheCtx.fillRect(px, py, this.camera.cellWidth, this.camera.cellHeight);
				}
			}
		}
	}

	private updateCameraFollow(
		target: PhysicalThing,
		preEffectsManager?: EffectManager,
		posEffectsManager?: EffectManager,
	): void {
		// Check if the target has an active movement effect; if so, interpolate in world space
		const worldPixel =
			this.getInterpolatedWorldPixel(target, preEffectsManager) ??
			this.getInterpolatedWorldPixel(target, posEffectsManager);

		if (worldPixel) {
			// Center on the interpolated world pixel (center of the sprite)
			this.camera.centerOnPixel(worldPixel.x + this.camera.cellWidth / 2, worldPixel.y + this.camera.cellHeight / 2);
		} else {
			// No movement effect — center on the target's current board position
			this.camera.centerOn(target.xy);
		}
	}

	private getInterpolatedWorldPixel(target: PhysicalThing, manager?: EffectManager): { x: number; y: number } | null {
		const cell = resolveEntityVisualCell(target, [manager]);
		if (!cell) return null;
		return { x: cell.x * this.camera.cellWidth, y: cell.y * this.camera.cellHeight };
	}

	private getFloorImage(board: Board, x: number, y: number): HTMLImageElement | HTMLCanvasElement | null {
		if (!board.defaultFloor) return this.gridTileImage;
		const visualId = board.terrainVisualIds[x][y];
		const cacheKey = `${board.defaultFloor}:${visualId}`;
		return this.floorImages.get(cacheKey) ?? this.gridTileImage;
	}

	render(board: Board, preEffectsManager?: EffectManager, posEffectsManager?: EffectManager): void {
		const { ctx, camera } = this;

		// Track elapsed time for tint pulsing
		const now = performance.now();
		if (this.lastRenderTimestamp !== null) {
			this.elapsedTime += now - this.lastRenderTimestamp;
		}
		this.lastRenderTimestamp = now;

		// Update camera to follow player's interpolated position
		if (this.followTarget) {
			this.updateCameraFollow(this.followTarget, preEffectsManager, posEffectsManager);
		}

		// Clear canvas
		ctx.fillStyle = "#000";
		ctx.fillRect(0, 0, camera.viewportWidth, camera.viewportHeight);

		const range = camera.getVisibleCellRange(board.width, board.height);
		this.lastVisibleCells = (range.maxX - range.minX + 1) * (range.maxY - range.minY + 1);

		const floorDrawCalls = this.drawFloor(board, camera);

		const { overrides, sortY } = this.computeMovementState(preEffectsManager, posEffectsManager);
		const drawables = this.collectDrawables(board, range, preEffectsManager, posEffectsManager);

		// Sort by draw order: low Y (top of screen) first = behind, high Y last = in front.
		// Within the same Y row, sort by ZOrder ascending. Moving things' rows come from the
		// movement-state map (see computeMovementState); fall back to the thing's board row.
		const rowOf = (t: PhysicalThing) => sortY.get(t) ?? t.xy.y;
		drawables.sort((a, b) => this.drawSortKey(a, rowOf(a)) - this.drawSortKey(b, rowOf(b)));

		const thingDrawCalls = this.drawThings(drawables, overrides, camera);

		// Draw visual-only MovementEffect things (not on the board)
		this.drawMovementEffectThings(preEffectsManager, overrides, drawables);
		this.drawMovementEffectThings(posEffectsManager, overrides, drawables);

		// Draw XY effect sprites on top of things
		this.drawXYEffects(preEffectsManager);
		this.drawXYEffects(posEffectsManager);

		this.lastFloorDrawCalls = floorDrawCalls;
		this.lastThingDrawCalls = thingDrawCalls;
		this.lastDrawableCount = drawables.length;
	}

	private drawSortKey(thing: PhysicalThing, sortY: number): number {
		return sortY * ROW_SORT_WEIGHT + thing.getZOrder();
	}

	// Draw floor layer from cache (single blit) or fall back to per-tile.
	private drawFloor(board: Board, camera: Camera): number {
		const { ctx } = this;
		let floorDrawCalls = 0;

		if (this.floorCacheCanvas) {
			const srcX = Math.max(0, camera.offsetX);
			const srcY = Math.max(0, camera.offsetY);
			const srcRight = Math.min(this.floorCacheCanvas.width, camera.offsetX + camera.viewportWidth);
			const srcBottom = Math.min(this.floorCacheCanvas.height, camera.offsetY + camera.viewportHeight);
			const srcW = srcRight - srcX;
			const srcH = srcBottom - srcY;
			if (srcW > 0 && srcH > 0) {
				const dstX = Math.max(0, -camera.offsetX);
				const dstY = Math.max(0, -camera.offsetY);
				ctx.drawImage(this.floorCacheCanvas, srcX, srcY, srcW, srcH, dstX, dstY, srcW, srcH);
				floorDrawCalls = 1;
			}
		} else {
			const { minX, minY, maxX, maxY } = camera.getVisibleCellRange(board.width, board.height);
			for (let y = minY; y <= maxY; y++) {
				for (let x = minX; x <= maxX; x++) {
					const pos = camera.boardToPixel(new XY(x, y));
					const floorImg = this.getFloorImage(board, x, y);
					if (floorImg) {
						ctx.drawImage(floorImg, pos.x, pos.y);
						floorDrawCalls++;
					} else {
						ctx.fillStyle = "#111";
						ctx.fillRect(pos.x, pos.y, camera.cellWidth, camera.cellHeight);
						floorDrawCalls++;
					}
				}
			}
		}

		return floorDrawCalls;
	}

	// Build interpolated positions and sort rows for things being animated by movement effects.
	private computeMovementState(
		preEffectsManager: EffectManager | undefined,
		posEffectsManager: EffectManager | undefined,
	): { overrides: Map<PhysicalThing, { x: number; y: number }>; sortY: Map<PhysicalThing, number> } {
		const overrides = new Map<PhysicalThing, { x: number; y: number }>();
		const sortY = new Map<PhysicalThing, number>();
		this.buildMovementOverrides(preEffectsManager, overrides, sortY);
		this.buildMovementOverrides(posEffectsManager, overrides, sortY);
		return { overrides, sortY };
	}

	private collectDrawables(
		board: Board,
		range: { minX: number; minY: number; maxX: number; maxY: number },
		preEffectsManager: EffectManager | undefined,
		posEffectsManager: EffectManager | undefined,
	): PhysicalThing[] {
		// While pre-effects play, show the pre-turn state: hide things added this turn
		// (corpse, gold), re-inject things removed this turn (dead mob) at their old cell.
		// Only no-movement things are touched, so the movement-render path stays intact.
		const showBeforeState = !!preEffectsManager && !preEffectsManager.isAllExpired();
		const movesThisTurn = (t: PhysicalThing): boolean =>
			entityHasMovementEffect(t, preEffectsManager) || entityHasMovementEffect(t, posEffectsManager);

		// Collect visible drawable things from all visible cells
		const drawables: PhysicalThing[] = [];
		for (let y = range.minY; y <= range.maxY; y++) {
			for (let x = range.minX; x <= range.maxX; x++) {
				const cell = board.cells[x][y];
				const visible = cell.getVisibleDrawables();
				for (let i = 0; i < visible.length; i++) {
					const thing = visible[i];
					if (showBeforeState && board.lastTurnAdditions.has(thing) && !movesThisTurn(thing)) continue;
					drawables.push(thing);
				}
			}
		}

		if (showBeforeState) {
			for (const removed of board.lastTurnRemovals) {
				if (!movesThisTurn(removed)) drawables.push(removed);
			}
		}

		return drawables;
	}

	private drawThings(
		drawables: PhysicalThing[],
		movementOverrides: Map<PhysicalThing, { x: number; y: number }>,
		camera: Camera,
	): number {
		const { ctx } = this;
		let thingDrawCalls = 0;

		for (const thing of drawables) {
			const sprite = this.drawableManager.getSprite(thing);
			if (!sprite) continue;

			// Use movement override position if this thing is being animated
			let basePixel;
			const override = movementOverrides.get(thing);
			if (override) {
				basePixel = override;
			} else {
				basePixel = camera.boardToPixel(thing.xy);
			}

			// Apply sprite offset
			const pos = {
				x: basePixel.x + sprite.offsetX * camera.cellWidth,
				y: basePixel.y - (sprite.height - 1) * camera.cellHeight - sprite.offsetY * camera.cellHeight,
			};

			const spriteWidth = sprite.width * camera.cellWidth;
			const spriteHeight = sprite.height * camera.cellHeight;

			if (!camera.isOnScreen(pos.x, pos.y, spriteWidth, spriteHeight)) continue;

			const imageToDraw = this.resolveThingImage(thing, sprite);
			if (imageToDraw) {
				ctx.drawImage(imageToDraw, pos.x, pos.y);
				thingDrawCalls++;
			}
		}

		return thingDrawCalls;
	}

	private resolveThingImage(
		thing: PhysicalThing,
		sprite: { image: HTMLImageElement | HTMLCanvasElement | null },
	): HTMLImageElement | HTMLCanvasElement | null {
		let imageToDraw: HTMLImageElement | HTMLCanvasElement | null = sprite.image;
		if (imageToDraw && isTintable(thing)) {
			const tint = thing.getTint();
			if (tint) {
				let effectiveAlpha: number;
				if (tint.minAlpha === tint.maxAlpha) {
					effectiveAlpha = tint.minAlpha;
				} else {
					const pulse = Math.sin(this.elapsedTime * 0.003) * 0.5 + 0.5; // 0..1
					effectiveAlpha = tint.minAlpha + pulse * (tint.maxAlpha - tint.minAlpha);
				}
				imageToDraw = getTintedImage(imageToDraw, tint.r, tint.g, tint.b, effectiveAlpha);
			}
		}
		return imageToDraw;
	}

	private buildMovementOverrides(
		manager: EffectManager | undefined,
		overrides: Map<PhysicalThing, { x: number; y: number }>,
		sortYMap: Map<PhysicalThing, number>,
	): void {
		if (!manager) return;
		const currentOrder = manager.getCurrentAnimationOrder();
		if (currentOrder === null) return;

		for (const effect of manager.getAllEffects()) {
			if (!(effect instanceof MovementEffect)) continue;
			if (effect.getAnimationOrder() !== currentOrder) continue;
			if (manager.isEffectExpired(effect)) continue;

			overrides.set(effect.thing, this.resolveScreenPixel(effect.thing, manager));
			// Default: pin moving things at max(origin, destination) so they draw on
			// top of tiles they're passing through. Entities that retract into a
			// higher-z-order northern neighbor (e.g. a sliding door into a wall) opt
			// out and sort at the topmost row touched by the move, so the wall
			// properly occludes them through the entire animation in either direction.
			const sortY = effect.thing.usesMinSortYWhileMoving()
				? Math.min(effect.origin.y, effect.destination.y)
				: Math.max(effect.origin.y, effect.destination.y);
			sortYMap.set(effect.thing, sortY);
		}

		// Lock pending-only things at their lowest-pending origin so they don't
		// render at the post-turn xy while waiting for their order.
		const lowestPending = new Map<PhysicalThing, MovementEffect>();
		for (const effect of manager.getAllEffects()) {
			if (!(effect instanceof MovementEffect)) continue;
			if (effect.getAnimationOrder() <= currentOrder) continue;
			if (overrides.has(effect.thing)) continue;
			const existing = lowestPending.get(effect.thing);
			if (!existing || effect.getAnimationOrder() < existing.getAnimationOrder()) {
				lowestPending.set(effect.thing, effect);
			}
		}
		for (const [thing, effect] of lowestPending) {
			overrides.set(thing, this.resolveScreenPixel(thing, manager));
			sortYMap.set(thing, effect.origin.y);
		}
	}

	private resolveScreenPixel(thing: PhysicalThing, manager: EffectManager): { x: number; y: number } {
		const cell = resolveEntityVisualCell(thing, [manager])!;
		return {
			x: cell.x * this.camera.cellWidth - this.camera.offsetX,
			y: cell.y * this.camera.cellHeight - this.camera.offsetY,
		};
	}

	private drawMovementEffectThings(
		manager: EffectManager | undefined,
		movementOverrides: Map<PhysicalThing, { x: number; y: number }>,
		boardDrawables: PhysicalThing[],
	): void {
		if (!manager || !manager.hasStarted()) return;

		const currentOrder = manager.getCurrentAnimationOrder();
		const boardThingSet = new Set<PhysicalThing>(boardDrawables);

		for (const effect of manager.getAllEffects()) {
			if (!(effect instanceof MovementEffect)) continue;
			if (effect.getAnimationOrder() !== currentOrder) continue;
			if (manager.isEffectExpired(effect)) continue;

			// Skip if this thing is already drawn from the board
			if (boardThingSet.has(effect.thing)) continue;

			const override = movementOverrides.get(effect.thing);
			if (!override) continue;

			const sprite = manager.getEffectSprite(effect);
			if (!sprite || !sprite.image) {
				// Fall back to the drawable manager sprite for the thing
				const dmSprite = this.drawableManager.getSprite(effect.thing);
				if (!dmSprite || !dmSprite.image) continue;

				const pos = {
					x: override.x + dmSprite.offsetX * this.camera.cellWidth,
					y: override.y - (dmSprite.height - 1) * this.camera.cellHeight - dmSprite.offsetY * this.camera.cellHeight,
				};
				this.ctx.drawImage(dmSprite.image, pos.x, pos.y);
				continue;
			}

			const pos = {
				x: override.x + sprite.offsetX * this.camera.cellWidth,
				y: override.y - (sprite.height - 1) * this.camera.cellHeight - sprite.offsetY * this.camera.cellHeight,
			};
			this.ctx.drawImage(sprite.image, pos.x, pos.y);
		}
	}

	renderMouseOverlays(
		hoveredXY: XY | null,
		isShift: boolean,
		aimCellXY: XY | null,
		dashedSquareImg: HTMLCanvasElement | HTMLImageElement | null,
		aimRedImg: HTMLCanvasElement | HTMLImageElement | null,
	): void {
		if (isShift && aimCellXY !== null && aimRedImg) {
			const pos = this.camera.boardToPixel(aimCellXY);
			this.ctx.drawImage(aimRedImg, pos.x, pos.y, this.camera.cellWidth, this.camera.cellHeight);
		} else if (hoveredXY !== null && dashedSquareImg) {
			const pos = this.camera.boardToPixel(hoveredXY);
			this.ctx.drawImage(dashedSquareImg, pos.x, pos.y, this.camera.cellWidth, this.camera.cellHeight);
		}
	}

	private drawXYEffects(manager: EffectManager | undefined): void {
		if (!manager || !manager.hasStarted()) return;

		const currentOrder = manager.getCurrentAnimationOrder();

		for (const effect of manager.getAllEffects()) {
			if (effect instanceof MovementEffect) continue;
			if (effect.getAnimationOrder() !== currentOrder) continue;

			const sprite = manager.getEffectSprite(effect);
			if (!sprite || !sprite.image) continue;

			if (effect instanceof XYEffect) {
				const drawXY = resolveXYEffectDrawPosition(effect, manager.getElapsedTime(effect));
				const pos = this.camera.boardToPixelWithSprite(drawXY, sprite);
				this.ctx.drawImage(sprite.image, pos.x, pos.y);
			}
		}
	}
}
